/**
 * Documents follow the project scope (card 4.1), pure. Same rule as Boards: a
 * page or folder is in a project by the project's `contains` link
 * (`document:<id>` / `doc_folder:<id>`, the Graph's ids); nothing is stamped
 * on the record.
 *
 *   a project    a node shows when it OR A FOLDER ABOVE IT is in the project;
 *                the folders above a shown node show too, so the tree stays
 *                connected (a page linked deep inside a folder keeps its path)
 *   No project   a node shows when neither it nor anything above it is in
 *                any project
 *   All          everything
 *   pending      (the active project not resolved yet) nothing, like Boards
 *
 * Only what is RENDERED is filtered: deletes, moves and new sort keys keep
 * working on the full tree (useSectionTree), so a hidden child is never left
 * behind by a delete or skipped by a move.
 */
import { ALL_PROJECTS, NO_PROJECT, normalizeScopeId } from '../../scope/projectScopeFilter';

export const docNodeId = (node) => `${node?.type === 'folder' ? 'doc_folder' : 'document'}:${node?.id}`;

/** The node and its ancestors, nearest first; cycle- and orphan-safe. */
function chainOf(node, byId) {
  const out = [];
  const seen = new Set();
  let cur = node;
  while (cur && !seen.has(String(cur.id))) {
    seen.add(String(cur.id));
    out.push(cur);
    cur = cur.parentId == null ? null : byId.get(String(cur.parentId));
  }
  return out;
}

export function filterDocsByProject(nodes = [], scope = {}) {
  const id = normalizeScopeId(scope.activeProjectId);
  if (id === ALL_PROJECTS) return nodes;
  const byId = new Map((nodes || []).map((n) => [String(n.id), n]));
  const memberIds = scope.memberIds || new Set();
  const assignedIds = scope.assignedIds || new Set();

  if (id === NO_PROJECT) {
    return nodes.filter((n) => !chainOf(n, byId).some((c) => assignedIds.has(docNodeId(c))));
  }
  if (!scope.activeProject || String(scope.activeProject.id) !== id) return [];

  // Documentation spaces (card 4.3): `scope.docSpace = { spaceId, assignment }`
  // keeps only the selected space — a page is in the space its project-linked
  // node (itself or the folder above it that is in the project) is assigned
  // to; no assignment = Main (`spaceId` null). Absent = every space.
  const space = scope.docSpace;
  const inSelectedSpace = (memberNode) => {
    if (!space) return true;
    const assigned = space.assignment?.get(docNodeId(memberNode)) || null;
    return assigned === (space.spaceId || null);
  };

  const keep = new Set();
  for (const n of nodes) {
    const chain = chainOf(n, byId);
    const member = chain.find((c) => memberIds.has(docNodeId(c)));
    if (!member || !inSelectedSpace(member)) continue;
    for (const c of chain) keep.add(String(c.id)); // the node and the folders above it
  }
  return nodes.filter((n) => keep.has(String(n.id)));
}
